import { CartProductDto } from '@up/data';

/**
 * Adjust the cart products by unit quantity
 * @param cartProducts Cart Product list.
 * @returns Cart Product list
 */
export function adjustCartProductByUnitQuantity(
  cartProducts: CartProductDto[]
): CartProductDto[] {
  const updatedCartProducts = [];
  cartProducts.forEach((product) => {
    for (let i = 0; i < product.quantity; i++) {
      const updatedProduct = Object.assign({}, product);
      updatedProduct.quantity = 1;
      updatedProduct.subtotal = product.subtotal / product.quantity;
      updatedCartProducts.push((updatedProduct as unknown) as CartProductDto);
    }
  });
  return updatedCartProducts;
}

/**
 * Sort cart products by price
 * @param cartProducts Cart Product list.
 * @returns Cart Product list
 */
export function sortCartProducts(
  cartProducts: CartProductDto[]
): CartProductDto[] {
  const sortedCartProductList = cartProducts.concat().sort(function (a, b) {
    return b.basePrice - a.basePrice;
  });
  return sortedCartProductList;
}

/**
 * get list of modifiers for cart product
 * @param cartProduct Cart Product.
 * @returns modifiers list
 */
export function getModifiers(cartProduct: CartProductDto): string[] {
  const modifiers: string[] = [];

  if (cartProduct.groups?.length) {
    for (const group of cartProduct.groups) {
      if (group.modifiers?.length) {
        for (const modifier of group.modifiers) {
          if (modifier.selected) {
            const quantity = modifier.selectedValue
              ? `${modifier.selectedValue}x `
              : '';
            modifiers.push(`${quantity}${modifier.modifierName}`);
          }
        }
      }
    }
  }

  return modifiers;
}

/**
 * get list of removed ingredients for cart product
 * @param cartProduct Cart Product.
 * @returns removed ingredients list
 */
export function getRemovedIngredients(cartProduct: CartProductDto): string[] {
  const { ingredients } = cartProduct;

  return ingredients
    ?.filter((ingredient) => !ingredient.selected)
    ?.map((ingredient) => `No ${ingredient.name}`);
}

/**
 * get Two-Dimensional list of bundles for cart products
 * @param cartProducts Cart Products.
 * @returns Cart Product Two-Dimensional list
 */
export function getBundles(cartProducts: CartProductDto[]): CartProductDto[][] {
  const bundleProducts = {}; 

  for (const item of cartProducts) {
    const { isBundle, bundleCombinationId } = item;

    if (isBundle || bundleCombinationId) {
      if (!bundleProducts[bundleCombinationId]) {
        bundleProducts[bundleCombinationId] = [];
      }

      if (isBundle) {
        bundleProducts[bundleCombinationId].unshift(item);
      } else {
        bundleProducts[bundleCombinationId].push(item);
      }
    }
  }

  const hasBundleDiscount = Object.values(bundleProducts)?.some((bundleProduct: CartProductDto[]) => {
    return bundleProduct.find(product => product.isBundle)
  });

  if(!hasBundleDiscount) {
    return cartProducts
      .filter((item) => item.bundleCombinationId)
      .map((item) => [item]);
  }

  return Object.values(bundleProducts);
}

/**
 * get Two-Dimensional list of single items for cart products
 * @param cartProducts Cart Product.
 * @returns Cart Product Two-Dimensional list
 */
export function getSingleItems(
  cartProducts: CartProductDto[]
): CartProductDto[][] {
  return cartProducts
    .filter((item) => !item.isBundle && !item.bundleCombinationId)
    .map((item) => [item]);
}

/**
 * get count for cart products where bundles are considered as 1 entity
 * @param cartProducts Cart Products.
 * @returns number of products
 */
export function countProducts(cartProducts: CartProductDto[]): number {
  if (!cartProducts || cartProducts?.length == 0) return 0;

  const singleItems = getSingleItems(cartProducts);
  const bundleItems = getBundles(cartProducts);

  const count = [...singleItems, ...bundleItems]?.reduce((prev, curr) => {
    const [product] = curr;
    return prev + product.quantity; 
  }, 0);
  
  return count;
}

/**
 * find bundle
 * @param cartProducts Cart Products.
 * @param bundleProduct bundle.
 * @returns list of products (bundle)
 */
export function findBundle(
  cartProducts: CartProductDto[],
  bundleProduct: CartProductDto
): CartProductDto[] {
  if (!cartProducts || cartProducts?.length == 0) return [];

  return getBundles(cartProducts)?.find(
    (bundlesList) =>
      bundlesList[0].bundleCombinationId === bundleProduct.bundleCombinationId
  );
}

/**
 * calculate subtotal
 * @param cartProduct Cart Product.
 * @returns subtotal
 */
export function calculateSubtotal(cartProduct: CartProductDto): number {
  if (!cartProduct) return 0;

  const { basePrice, isBundle } = cartProduct;
  let modifiersPrice = 0;

  if (isBundle) return basePrice;

  if (cartProduct.groups?.length) {
    for (const group of cartProduct.groups) {
      if (group.modifiers?.length) {
        for (const modifier of group.modifiers) {
          if (modifier.selected) {
            const modifierPrice = modifier.selectedValue
              ? modifier.selectedValue * modifier.price
              : modifier.price;
            modifiersPrice = modifiersPrice + modifierPrice;
          }
        }
      }
    }
  }

  return basePrice + modifiersPrice;
}

export function getLastCartProductClickedSequence(
  currentClickedSequence,
  cartProducts: CartProductDto[]
) {
  let cartProductsWithClickedSequence = [];
  cartProductsWithClickedSequence = cartProducts.filter((x) => x.clickedSequence);
  cartProductsWithClickedSequence?.forEach((x) => {
    if(x.clickedSequence > currentClickedSequence) {
      currentClickedSequence = x.clickedSequence;
    }
  })

  return currentClickedSequence;
}